import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'vedaya_setup_progress';
const BANNER_DISMISSED_KEY = 'vedaya_setup_banner_dismissed';

const OnboardingContext = createContext(null);

const requiredSteps = ['teams', 'identity', 'channels', 'routing', 'workflow'];
const requiredChecks = {
  teams: ['team_created', 'admin_assigned', 'member_added'],
  identity: ['idp_connected', 'users_synced', 'sso_tested'],
  channels: ['channel_connected', 'channel_mode_set', 'test_message_sent'],
  routing: ['routing_rule_created', 'approval_chain_set', 'escalation_defined'],
  workflow: ['workflow_created', 'workflow_tested', 'workflow_published']
};

export function OnboardingProvider({ children }) {
  const [setupProgress, setSetupProgress] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const [bannerDismissed, setBannerDismissed] = useState(() => {
    try {
      return localStorage.getItem(BANNER_DISMISSED_KEY) === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(setupProgress));
    } catch (e) {
      console.error('Failed to save setup progress:', e);
    }
  }, [setupProgress]);

  useEffect(() => {
    try {
      localStorage.setItem(BANNER_DISMISSED_KEY, String(bannerDismissed));
    } catch (e) {
      console.error('Failed to save banner state:', e);
    }
  }, [bannerDismissed]);

  const isStepComplete = useCallback((stepId) => {
    const stepProgress = setupProgress[stepId] || {};
    const checks = requiredChecks[stepId] || [];
    return checks.every(checkId => stepProgress[checkId] === true);
  }, [setupProgress]);

  const isSetupComplete = useCallback(() => {
    return requiredSteps.every(stepId => isStepComplete(stepId));
  }, [isStepComplete]);

  const getCompletedStepsCount = useCallback(() => {
    return requiredSteps.filter(stepId => isStepComplete(stepId)).length;
  }, [isStepComplete]);

  const getSetupPercentage = useCallback(() => {
    const totalChecks = Object.values(requiredChecks).flat().length;
    const completedChecks = requiredSteps.reduce((count, stepId) => {
      const stepProgress = setupProgress[stepId] || {};
      const checks = requiredChecks[stepId] || [];
      return count + checks.filter(checkId => stepProgress[checkId] === true).length;
    }, 0);
    return Math.round((completedChecks / totalChecks) * 100);
  }, [setupProgress]);

  const updateStepProgress = useCallback((stepId, checkId, value) => {
    setSetupProgress(prev => ({
      ...prev,
      [stepId]: {
        ...prev[stepId],
        [checkId]: value
      }
    }));
  }, []);

  const dismissBanner = useCallback(() => {
    setBannerDismissed(true);
  }, []);

  const resetBannerDismissal = useCallback(() => {
    setBannerDismissed(false);
  }, []);

  const value = {
    setupProgress,
    setSetupProgress,
    isSetupComplete,
    isStepComplete,
    getCompletedStepsCount,
    getSetupPercentage,
    updateStepProgress,
    bannerDismissed,
    dismissBanner,
    resetBannerDismissal,
    requiredStepsCount: requiredSteps.length
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
}

export default OnboardingContext;
